import { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { X, Check, Loader2, Tag } from 'lucide-react';

const PurchaseCreditsModal = ({ onClose, onCreditsAdded }) => {
  const [loading, setLoading] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [promoCode, setPromoCode] = useState('');

  const packages = [
    {
      id: 'individual',
      name: 'Single Assessment',
      price: 10,
      credits: 1,
      description: 'Perfect for trying out',
      popular: false
    },
    {
      id: 'small_pack',
      name: 'Small Pack',
      price: 90,
      credits: 10,
      description: 'Save 10% - Best for small teams',
      popular: true,
      savings: '$10'
    },
    {
      id: 'medium_pack',
      name: 'Medium Pack',
      price: 425,
      credits: 50,
      description: 'Save 15% - Great for growing organizations',
      popular: false,
      savings: '$75'
    },
    {
      id: 'large_pack',
      name: 'Large Pack',
      price: 800,
      credits: 100,
      description: 'Save 20% - Best value for large organizations',
      popular: false,
      savings: '$200'
    }
  ];

  const handlePurchase = async (packageType) => {
    setLoading(true);
    setSelectedPackage(packageType);

    try {
      let response;
      
      if (promoCode.trim()) {
        // Attempt checkout with promo code - validation happens at checkout
        try {
          response = await axios.post(`${API}/payment/create-checkout-with-promo`, {
            code: promoCode,
            package_type: packageType
          });
          
          // If it's a free redemption, show success and close
          if (response.data.free_redemption) {
            toast.success(response.data.message);
            if (onCreditsAdded) {
              onCreditsAdded(response.data.credits_added);
            }
            onClose();
            return;
          }
        } catch (promoError) {
          // Show promo error but don't stop - let user know
          toast.error(promoError.response?.data?.detail || 'Invalid promo code');
          setLoading(false);
          setSelectedPackage(null);
          return;
        }
      } else {
        // Regular checkout without promo code
        response = await axios.post(`${API}/payment/create-checkout`, {
          package_type: packageType
        });
      }

      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create checkout session');
      setLoading(false);
      setSelectedPackage(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10"
          data-testid="close-purchase-modal"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="p-8">
          <div className="text-center mb-8">
            <h2 className="text-4xl font-bold mb-3 text-gray-800">Choose Your Package</h2>
            <p className="text-xl text-gray-600">Select the perfect option for your needs</p>
          </div>

          {/* Promo Code Section - Enter at checkout */}
          <div className="mb-8 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-100">
            <div className="flex items-center gap-2 mb-3">
              <Tag className="h-5 w-5 text-indigo-600" />
              <span className="font-semibold text-gray-700">Have a promo code?</span>
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Enter promo code"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                className="uppercase max-w-xs"
                data-testid="promo-code-input"
              />
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Promo code will be validated when you proceed to checkout
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {packages.map((pkg) => (
              <Card
                key={pkg.id}
                className={`relative hover:shadow-xl transition-shadow ${
                  pkg.popular ? 'border-4 border-indigo-500' : 'border-2'
                }`}
                data-testid={`package-${pkg.id}`}
              >
                {pkg.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-indigo-600 text-white px-4 py-1">Most Popular</Badge>
                  </div>
                )}

                <CardHeader>
                  <CardTitle className="text-2xl">{pkg.name}</CardTitle>
                  <CardDescription className="text-base">{pkg.description}</CardDescription>
                </CardHeader>

                <CardContent>
                  <div className="mb-6">
                    <div className="flex items-baseline gap-2 mb-2">
                      <span className="text-5xl font-bold text-indigo-600">${pkg.price}</span>
                      {pkg.savings && (
                        <span className="text-green-600 font-semibold">{pkg.savings} off</span>
                      )}
                    </div>
                    <div className="text-gray-600">
                      <span className="text-2xl font-semibold">{pkg.credits}</span> Assessment{pkg.credits > 1 ? 's' : ''}
                    </div>
                    {pkg.credits > 1 && (
                      <div className="text-sm text-gray-500 mt-1">
                        ${(pkg.price / pkg.credits).toFixed(2)} per assessment
                      </div>
                    )}
                  </div>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-600 mt-0.5" />
                      <span>Complete BUILDR assessment</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-600 mt-0.5" />
                      <span>Detailed results & role recommendations</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-600 mt-0.5" />
                      <span>Email results summary</span>
                    </div>
                    {pkg.credits > 1 && (
                      <div className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-green-600 mt-0.5" />
                        <span>Share credits with team members</span>
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={() => handlePurchase(pkg.id)}
                    disabled={loading}
                    className={`w-full ${
                      pkg.popular
                        ? 'bg-indigo-600 hover:bg-indigo-700'
                        : 'bg-gray-800 hover:bg-gray-900'
                    }`}
                    data-testid={`buy-${pkg.id}`}
                  >
                    {loading && selectedPackage === pkg.id ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : promoCode.trim() ? (
                      `Apply Code & Checkout`
                    ) : (
                      `Purchase ${pkg.credits} Credit${pkg.credits > 1 ? 's' : ''}`
                    )}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-8 text-center text-sm text-gray-500">
            <p>Secure payment powered by Stripe</p>
            <p className="mt-2">Credits never expire and can be used anytime</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseCreditsModal;
